/**
 * `BX.Promise`: a deferred written before the native Promise was available.
 *
 * It is not Promises/A+: `then()` hands out one preallocated next promise instead of a new one,
 * handlers run synchronously inside `fulfill()` and `reject()`, and the promise is settled from
 * the outside. Every one of those traits has callers relying on it, so the code below keeps them
 * as they were; `test/old/core/promise.test.ts` pins them.
 *
 * **This module must not use the kernel**, not even through an import. It is built twice: into
 * `core.js` and on its own into `core_promise.js`, which `skip_core` extensions load on pages that
 * have no kernel at all. So the only imports allowed here are type imports, and `BX` is whatever
 * the page has, created if it has nothing.
 *
 * Written as a constructor function with methods assigned to the prototype, like `hint.ts`: the
 * methods have to stay enumerable members of the prototype, which `class` syntax would not give.
 * The symbol is published by assignment rather than by export: the standalone file has no kernel
 * to write the exports into `BX`, and inside `core.js` the `BX.Promise` typing comes from
 * `@type/legacy-promise.d.ts`, which a generated declaration would shadow.
 *
 * @deprecated
 * @use Promise
 */

import { type LegacyGlobal } from '../internal/global';

type Handler = (value: any) => any;

interface LegacyPromise
{
	state: boolean | null;
	value: any;
	reason: any;
	next: LegacyPromise | null;
	ctx: object;
	onFulfilled: Handler[];
	onRejected: Handler[];
	timer?: ReturnType<typeof setTimeout>;

	fulfill(value?: any): void;
	reject(reason?: any): void;
	then(onFulfilled?: any, onRejected?: any): LegacyPromise;
	catch(onRejected?: any): LegacyPromise;
	setAutoResolve(way: boolean, ms?: number): void;
	cancelAutoResolve(): void;
	resolve(x: any): void;
	toString(): string;
	execute(): void;
	checkState(): void;
}

const win: any = window;
win.BX = win.BX || {};

const BX: LegacyGlobal = win.BX;

// `fn` is reserved for a future executor and is ignored.
function BxPromise(this: LegacyPromise, fn?: unknown, ctx?: object): void
{
	this.state = null;
	this.value = null;
	this.reason = null;
	this.next = null;
	this.ctx = ctx || this;

	this.onFulfilled = [];
	this.onRejected = [];
}

BxPromise.prototype.fulfill = function(this: LegacyPromise, value: any): void
{
	this.checkState();

	this.value = value;
	this.state = true;
	this.execute();
};

BxPromise.prototype.reject = function(this: LegacyPromise, reason: any): void
{
	this.checkState();

	this.reason = reason;
	this.state = false;
	this.execute();
};

// eslint-disable-next-line unicorn/no-thenable -- `then` is the method this deferred exists for
BxPromise.prototype.then = function(this: LegacyPromise, onFulfilled?: any, onRejected?: any): LegacyPromise
{
	if (typeof onFulfilled === 'function' || onFulfilled instanceof Function)
	{
		this.onFulfilled.push(onFulfilled);
	}

	if (typeof onRejected === 'function' || onRejected instanceof Function)
	{
		this.onRejected.push(onRejected);
	}

	if (this.next === null)
	{
		this.next = new BX.Promise(null, this.ctx);
	}

	// a promise settled already runs its handlers right away
	if (this.state !== null)
	{
		this.execute();
	}

	return this.next as LegacyPromise;
};

BxPromise.prototype.catch = function(this: LegacyPromise, onRejected?: any): LegacyPromise
{
	if (typeof onRejected === 'function' || onRejected instanceof Function)
	{
		this.onRejected.push(onRejected);
	}

	if (this.next === null)
	{
		this.next = new BX.Promise(null, this.ctx);
	}

	if (this.state !== null)
	{
		this.execute();
	}

	return this.next as LegacyPromise;
};

BxPromise.prototype.setAutoResolve = function(this: LegacyPromise, way: boolean, ms?: number): void
{
	this.timer = setTimeout(() => {
		if (this.state === null)
		{
			this[way ? 'fulfill' : 'reject']();
		}
	}, ms || 15);
};

BxPromise.prototype.cancelAutoResolve = function(this: LegacyPromise): void
{
	clearTimeout(this.timer);
};

/**
 * Settles the promise with `x`. A `BX.Promise` passed as `x` pauses the chain until it settles,
 * which is what lets a handler return one from `then()`. It is recognised by `toString()`, not by
 * `instanceof`, so a promise from another copy of this file is recognised too.
 */
BxPromise.prototype.resolve = function(this: LegacyPromise, x: any): void
{
	if (this === x)
	{
		this.reject(new TypeError('Promise cannot fulfill or reject itself'));
	}
	else if (x && x.toString() === '[object BX.Promise]')
	{
		// eslint-disable-next-line promise/catch-or-return -- a BX.Promise reports failure to the second handler
		x.then((value: any) => {
			this.fulfill(value);
		}, (reason: any) => {
			this.reject(reason);
		});
	}
	else
	{
		this.fulfill(x);
	}
};

BxPromise.prototype.toString = function(): string
{
	return '[object BX.Promise]';
};

BxPromise.prototype.execute = function(this: LegacyPromise): void
{
	// then() must not run the handlers before the promise is settled
	if (this.state === null)
	{
		return;
	}

	let value;
	let reason;
	let x;

	if (this.state === true)
	{
		if (this.onFulfilled.length > 0)
		{
			try
			{
				for (let k = 0; k < this.onFulfilled.length; k++)
				{
					x = this.onFulfilled[k].apply(this.ctx, [this.value]);
					if (typeof x !== 'undefined')
					{
						value = x;
					}
				}
			}
			catch (e)
			{
				if ('console' in window)
				{
					console.dir(e);
				}

				if (typeof BX.debug !== 'undefined')
				{
					BX.debug(e);
				}

				reason = e;
			}
		}
		else
		{
			value = this.value;
		}
	}
	else if (this.state === false)
	{
		if (this.onRejected.length > 0)
		{
			try
			{
				for (let k = 0; k < this.onRejected.length; k++)
				{
					x = this.onRejected[k].apply(this.ctx, [this.reason]);
					if (typeof x !== 'undefined')
					{
						value = x;
					}
				}
			}
			catch (e)
			{
				if ('console' in window)
				{
					console.dir(e);
				}

				if (typeof BX.debug !== 'undefined')
				{
					BX.debug(e);
				}

				reason = e;
			}
		}
		else
		{
			reason = this.reason;
		}
	}

	if (this.next !== null)
	{
		if (typeof reason !== 'undefined')
		{
			this.next.reject(reason);
		}
		else if (typeof value !== 'undefined')
		{
			this.next.resolve(value);
		}
	}
};

BxPromise.prototype.checkState = function(this: LegacyPromise): void
{
	if (this.state !== null)
	{
		throw new Error('You can not do fulfill() or reject() multiple times');
	}
};

BX.Promise = BxPromise;
